import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';

export const folderUserKeys = {
  all: ['folderUsers'] as const,
  lists: () => [...folderUserKeys.all, 'list'] as const,
  list: (folderId: number) => [...folderUserKeys.lists(), folderId] as const,
};

/**
 * Fetch all users associated with a folder
 */
export const useFolderUsersQuery = (folderId: number, enabled = true) => {
  return useQuery({
    queryKey: folderUserKeys.list(folderId),
    queryFn: () => apiClient.getFolderUsers(folderId),
    enabled: !!folderId && enabled,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Assign an existing project user to a folder (auto-assigned Viewer role)
 */
export const useAssignUserToFolderMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, userId }: { folderId: number; userId: number }) =>
      apiClient.assignUserToFolder(folderId, userId),
    onSuccess: (_, { folderId }) => {
      queryClient.invalidateQueries({ queryKey: folderUserKeys.list(folderId) });
      toast.success('User added to folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add user');
    },
  });
};

/**
 * Change a user's role on a folder
 */
export const useUpdateFolderUserRoleMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, userId, roleId }: { folderId: number; userId: number; roleId: number }) =>
      apiClient.updateFolderUserRole(folderId, userId, roleId),
    onSuccess: (_, { folderId }) => {
      queryClient.invalidateQueries({ queryKey: folderUserKeys.list(folderId) });
      toast.success('Role updated');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update role');
    },
  });
};

/**
 * Remove a user from a folder
 */
export const useRemoveFolderUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ folderId, userId }: { folderId: number; userId: number }) =>
      apiClient.removeFolderUser(folderId, userId),
    onSuccess: (_, { folderId }) => {
      queryClient.invalidateQueries({ queryKey: folderUserKeys.list(folderId) });
      toast.success('User removed from folder');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to remove user');
    },
  });
};
